<script setup lang="ts">
import { computed, ref } from 'vue';
import { useBlockEditorLibrary } from '#layers/block-engine';
import { IconPicker } from '#layers/ui';

interface Props {
	hasChanges?: boolean;
	namePlaceholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
	hasChanges: false,
});

defineEmits<{
	reset: [];
	saveForEveryone: [];
}>();

const open = defineModel<boolean>('open', { default: false });

const blockLibrary = useBlockEditorLibrary();

const { filteredBlocks: layoutBlocks } = blockLibrary.useFilteredBlocks({ filter: (block) => block.type === 'listing-layout' });

const layoutId = defineModel<string>('layoutId', { default: '$builtin:LayoutTable' });
const name = defineModel<string>('name');
const icon = defineModel<string>('icon');

const layoutBlock = computed(() => blockLibrary.getBlock(layoutId.value));
const defaultIcon = computed(() => layoutBlock.value?.icon ?? undefined);

const state = ref({});
</script>

<template>
	<DPopover v-model:open="open" :ui="{ content: 'overflow-hidden' }">
		<DTooltip :text="$t('listing.views.display_options.title')" :disabled="open">
			<DButton
				color="neutral"
				variant="outline"
				:aria-label="$t('listing.views.display_options.title')"
				square
				:pressed="open"
				size="sm"
				data-test-id="display-options-button"
			>
				<template #leading>
					<DChip
						:show="props.hasChanges"
						inset
						size="sm"
						:ui="{ base: '-me-1 -mt-1 ring-(--ui-bg-elevated)' }"
						data-test-id="display-options-change-chip"
					>
						<DIcon name="lucide:sliders-horizontal" size="16" />
					</DChip>
				</template>
			</DButton>
		</DTooltip>
		<template #content>
			<DAnimatedSheet class="flex max-w-md flex-col">
				<div class="space-y-3 p-4 pb-2">
					<DTileRadioGroup
						v-model="layoutId"
						class="grid-cols-3"
						:items="layoutBlocks"
						value-key="id"
						label-key="name"
						size="xs"
					/>
					<DSeparator />
					<DForm :state layout="side-by-side" size="sm" auto-focus-first-input>
						<DFormField :label="$t('listing.views.name_label')" :error-pattern="/name|icon/">
							<DFieldGroup>
								<IconPicker
									v-model="icon"
									:placeholder-icon="defaultIcon"
									spacing="comfortable"
								/>
								<DInput
									v-model="name"
									data-test-id="view-name-input"
									:placeholder="namePlaceholder"
									autofocus
									autocomplete="off"
									class="w-full"
								/>
							</DFieldGroup>
						</DFormField>
						<slot />
					</DForm>
					<DSeparator v-if="props.hasChanges" />
				</div>
				<div v-if="props.hasChanges" class="flex items-center justify-end gap-2 px-1.5 pb-1.5" data-test-id="display-options-save-footer">
					<DTooltip :text="$t('listing.views.save.reset_tooltip')">
						<DButton
							variant="ghost"
							color="neutral"
							size="sm"
							data-test-id="display-options-reset-button"
							@click="$emit('reset')"
						>
							{{ $t('listing.views.save.reset') }}
						</DButton>
					</DTooltip>
					<DButton
						variant="ghost"
						color="primary"
						size="sm"
						data-test-id="display-options-save-for-everyone-button"
						@click="$emit('saveForEveryone')"
					>
						{{ $t('listing.views.save.save_for_everyone') }}
					</DButton>
				</div>
			</DAnimatedSheet>
		</template>
	</DPopover>
</template>
